import React from 'react';
import { LocationResult, MovePreferences } from '../types/weather';
import { Percent, Thermometer, CloudRain, Calendar, Trophy, MapPin } from 'lucide-react';

interface LocationComparisonProps {
  results: LocationResult[];
  preferences: MovePreferences;
}

export const LocationComparison: React.FC<LocationComparisonProps> = ({ results, preferences }) => {
  // Handle empty results
  if (!results || results.length === 0) {
    return (
      <div className="card text-center py-12">
        <div className="text-gray-400 mb-4">
          <MapPin className="h-16 w-16 mx-auto" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Locations Found</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          We couldn't find any locations matching your preferences. Try adjusting your criteria or adding different locations.
        </p>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <p>Tips:</p>
          <ul className="mt-2 space-y-1">
            <li>• Try broader temperature preferences</li>
            <li>• Consider different precipitation settings</li>
            <li>• Add more locations to compare</li>
          </ul>
        </div>
      </div>
    );
  }

  const sortedResults = [...results].sort((a, b) => b.perfectDaysScore - a.perfectDaysScore);
  const bestLocation = sortedResults[0];

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
  };

  const getTemperatureDescription = (temp: string) => {
    const descriptions = {
      'hot': 'Hot weather (80°F+)',
      'warm': 'Warm weather (65-80°F)',
      'cool': 'Cool weather (45-65°F)',
      'cold': 'Cold weather (20-45°F)',
      'freezing': 'Freezing weather (below 32°F)'
    };
    return descriptions[temp as keyof typeof descriptions];
  };

  const getRainDescription = (rain: string) => {
    const descriptions = {
      'dry': 'Dry climate (minimal precipitation)',
      'occasional-rain': 'Occasional precipitation (moderate)',
      'regular-rain': 'Regular precipitation (frequent)'
    };
    return descriptions[rain as keyof typeof descriptions];
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="card bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-blue-600 rounded-lg">
            <Trophy className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Best Match: {bestLocation?.location}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              {bestLocation?.summary}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Thermometer className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {getTemperatureDescription(preferences.temperature)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <CloudRain className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {getRainDescription(preferences.precipitation)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {preferences.priority.replace('-', ' ')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="card">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <MapPin className="h-5 w-5 mr-2 text-blue-600" />
          Location Comparison
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Location</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Perfect Days Score</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Avg Temp</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Precipitation Days/Year</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Best Months</th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((result, index) => (
                <tr key={result.location} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      {index === 0 && (
                        <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                          <Trophy className="h-3 w-3 text-yellow-800" />
                        </div>
                      )}
                      {index > 0 && (
                        <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                          {index + 1}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">{result.location}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {result.state && `${result.state}, `}{result.country}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(result.perfectDaysScore)}`}>
                        <Percent className="h-3 w-3 mr-1" />
                        {result.perfectDaysScore}%
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center text-gray-700 dark:text-gray-300">
                    {result.details.averageTemp}°F
                  </td>
                  <td className="py-4 px-4 text-center text-gray-700 dark:text-gray-300">
                    {result.details.rainyDays}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {result.details.bestMonths.slice(0, 2).join(', ')}
                      {result.details.bestMonths.length > 2 && '...'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {sortedResults.map((result, index) => (
          <div key={result.location} className={`card ${index === 0 ? 'ring-2 ring-blue-500 ring-opacity-50' : ''}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                {index === 0 && (
                  <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                    <Trophy className="h-4 w-4 text-yellow-800" />
                  </div>
                )}
                {index > 0 && (
                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-400">
                    {index + 1}
                  </div>
                )}
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{result.location}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {result.state && `${result.state}, `}{result.country}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(result.perfectDaysScore)}`}>
                {result.perfectDaysScore}% match
              </div>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
              {result.summary}
            </p>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium text-gray-900 dark:text-white mb-1">Temperature Range</div>
                <div className="text-gray-600 dark:text-gray-400">
                  {result.details.temperatureRange.min}°F - {result.details.temperatureRange.max}°F
                </div>
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white mb-1">Dry Days/Year</div>
                <div className="text-gray-600 dark:text-gray-400">
                  {result.details.dryDays} days
                </div>
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white mb-1">Best Months</div>
                <div className="text-gray-600 dark:text-gray-400">
                  {result.details.bestMonths.join(', ')}
                </div>
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white mb-1">Challenging Months</div>
                <div className="text-gray-600 dark:text-gray-400">
                  {result.details.worstMonths.join(', ')}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card bg-gray-50 dark:bg-gray-800/50">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-2">How we calculate your perfect days score:</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          We analyze 20+ years of daily weather data and count the percentage of days that match your preferences for 
          temperature ({getTemperatureDescription(preferences.temperature).toLowerCase()}) and 
          precipitation ({getRainDescription(preferences.precipitation).toLowerCase()}). 
          A higher percentage means more days throughout the year will feel perfect to you.
        </p>
      </div>
    </div>
  );
};
